import { useCallback, useEffect, useState } from "react";

import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";

export const useScrollableTable = <T>({
  fetchItems,
  errorMessage = "",
}: {
  fetchItems: (offset: number) => Promise<{ items: Array<T>; count: number }>;
  errorMessage?: string;
}) => {
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
  });

  const [initLoading, setInitLoading] = useState(true);
  const [loadingMoreItems, setLoadingMoreItems] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState(0);
  const [items, setItems] = useState<Array<T>>([]);

  const loadMoreItems = useCallback(
    async (offset: number = 0) => {
      if (!initLoading && offset > 0 && items.length >= totalItems) {
        return;
      }

      setLoadingMoreItems(true);

      try {
        const { items: newItems, count } = await fetchItems(offset);

        setItems((prev) => [...prev, ...newItems]);
        setTotalItems(count);
      } catch (e) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `${errorMessage}`,
          details: createToastErrorDetails(e),
        });
      } finally {
        setInitLoading(false);
        setLoadingMoreItems(false);
      }
    },
    [initLoading, loadingMoreItems, totalItems],
  );

  useEffect(() => {
    loadMoreItems(totalItems);
  }, []);

  useEffect(() => {
    if (isIntersecting && !loadingMoreItems && !initLoading) {
      loadMoreItems(items.length);
    }
  }, [isIntersecting]);

  return {
    ref,
    initLoading,
    loadingMoreItems,
    totalItems,
    items,
  };
};
